/**
 * Render an article body with inline body-image controls between
 * paragraphs. The read-only view of the article ONLY — the RichText
 * editor's WYSIWYG isn't touched, because inline React controls in
 * the middle of contentEditable content are a footgun.
 *
 * Structure per paragraph slot (0..N-1):
 *   <p>…paragraph HTML…</p>
 *   [BodyImageStrip]     ← images placed at paragraph_index === slot
 *   [+ image button]     ← opens the add-image modal
 *
 * The paragraph slot at index === paragraph_count is the "tail" —
 * images with an out-of-range index render there so nothing is
 * silently dropped when body content shrinks.
 */
import { useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X, Upload, Sparkles, Image as ImageIcon, Images, Loader2, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import PasteImageButton from "@/components/PasteImageButton";
import StockPhotoPicker from "@/components/StockPhotoPicker";
import AddToStockOption, { useAddToStock } from "@/components/AddToStockOption";
import { SourceStockButton, useSourceStock } from "@/components/SourceToStock";
import { ArticleStockButton, useArticleStock } from "@/components/ArticleImageToStock";
import { usePasteImage } from "@/hooks/usePasteImage";
import { courtesyPrefill } from "@/lib/courtesyNames";

// Legend option list mirrors the featured-image field on the article
// form so editors get the exact same choices in both places.
const LEGEND_OPTIONS = [
  { value: "", label: "No legend" },
  { value: "source_name", label: "Source outlet name" },
  { value: "courtesy", label: "Photo courtesy of…" },
  { value: "custom", label: "Custom text…" },
];

// `courtesyNames` — "Photo courtesy of" suggestions for this article
// (lib/courtesyNames.sourceNameSuggestions, computed by the Article
// page). The first one pre-fills the name when an editor picks
// "Photo courtesy of…" for a body image, as the featured image does.
export function ArticleBodyWithImages({ article, canEdit, courtesyNames = [] }) {
  const paragraphs = useMemo(
    () => splitParagraphs(article.body_html || ""),
    [article.body_html],
  );
  const imagesByIdx = useMemo(() => {
    const m = new Map();
    (article.body_images || []).forEach((img) => {
      const idx = Math.min(paragraphs.length, Math.max(0, img.paragraph_index || 0));
      if (!m.has(idx)) m.set(idx, []);
      m.get(idx).push(img);
    });
    return m;
  }, [article.body_images, paragraphs.length]);

  const [addAt, setAddAt] = useState(null);   // slot index to add at

  return (
    <div className="prose-editorial" data-testid="article-body-with-images">
      {paragraphs.map((html, i) => (
        <div key={i}>
          <div dangerouslySetInnerHTML={{ __html: html }} />
          <BodyImageStrip
            article={article}
            images={imagesByIdx.get(i) || []}
            canEdit={canEdit}
            courtesyNames={courtesyNames}
          />
          {canEdit && (
            <AddImageButton
              onClick={() => setAddAt(i)}
              testId={`body-image-add-after-${i}`}
            />
          )}
        </div>
      ))}
      {/* Tail: images whose paragraph_index >= paragraph count. */}
      <BodyImageStrip
        article={article}
        images={imagesByIdx.get(paragraphs.length) || []}
        canEdit={canEdit}
        courtesyNames={courtesyNames}
      />
      {addAt != null && (
        <AddBodyImageModal
          article={article}
          paragraphIndex={addAt}
          onClose={() => setAddAt(null)}
        />
      )}
    </div>
  );
}

// Very small paragraph splitter — matches every <p>...</p> block
// (case-insensitive, non-greedy). Non-<p> HTML (headings, lists) is
// attached to the following paragraph so it renders in the same slot.
// A future upgrade could split on <h*>/<ul>/<ol> too, but simple
// paragraph slots cover the common editor case.
function splitParagraphs(html) {
  const parts = [];
  const re = /<p\b[^>]*>[\s\S]*?<\/p\s*>/gi;
  let last = 0;
  let m;
  let buffer = "";
  while ((m = re.exec(html)) !== null) {
    // Non-<p> content between the previous match and this one goes
    // into the running buffer, then gets prepended to the current
    // paragraph so decorative HTML sits with its paragraph.
    buffer += html.slice(last, m.index);
    parts.push(buffer + m[0]);
    buffer = "";
    last = m.index + m[0].length;
  }
  const trailing = buffer + html.slice(last);
  if (trailing.trim()) parts.push(trailing);
  return parts.length ? parts : [html];
}

function AddImageButton({ onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="my-2 inline-flex items-center gap-1 text-[10px] text-[var(--ink-muted)] hover:text-[var(--ink)] border border-dashed border-[var(--border)] hover:border-[var(--ink)] px-2 py-1 opacity-0 hover:opacity-100 focus:opacity-100 group-hover:opacity-100 transition-opacity"
      style={{ opacity: 0.4 }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.4)}
    >
      <Plus className="w-3 h-3" /> Add image after this paragraph
    </button>
  );
}

function BodyImageStrip({ article, images, canEdit, courtesyNames }) {
  if (!images.length) return null;
  return (
    <div className="my-4 space-y-4" data-testid="body-image-strip">
      {images.map((img) => (
        <BodyImageCard
          key={img.id}
          article={article}
          img={img}
          canEdit={canEdit}
          courtesyNames={courtesyNames}
        />
      ))}
    </div>
  );
}

function BodyImageCard({ article, img, canEdit, courtesyNames }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  // "Add to Stock" for this body image (live at once).
  const artStock = useArticleStock(article);
  const legendText = (() => {
    const key = img.image_legend;
    const text = (img.image_legend_custom_text || "").trim();
    if (key === "custom") return text;
    if (key === "courtesy") return text ? `Photo courtesy of ${text}` : "";
    if (key === "source_name") {
      try {
        return article.sources_used?.[0]
          ? new URL(article.sources_used[0]).hostname
          : "";
      } catch {
        return "";
      }
    }
    return "";
  })();
  const del = useMutation({
    mutationFn: () => api.deleteBodyImage(article.id, img.id),
    onSuccess: () => {
      toast.success("Image removed");
      qc.invalidateQueries({ queryKey: ["article", article.id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });
  return (
    <figure className="border border-[var(--border)] bg-[var(--paper)]" data-testid={`body-image-${img.id}`}>
      <img src={img.url} alt={img.alt || ""} className="w-full block" loading="lazy" />
      {legendText && (
        <figcaption className="text-xs text-[var(--ink-muted)] italic px-3 py-2 border-t border-[var(--border)]">
          {legendText}
        </figcaption>
      )}
      {canEdit && (
        <div className="px-3 py-2 border-t border-[var(--border)] flex items-center gap-2 flex-wrap text-[11px]">
          {editing ? (
            <BodyImageEditor
              article={article}
              img={img}
              courtesyNames={courtesyNames}
              onDone={() => setEditing(false)}
            />
          ) : (
            <>
              <button
                data-testid={`body-image-edit-${img.id}`}
                onClick={() => setEditing(true)}
                className="text-[var(--ink-muted)] hover:text-[var(--ink)] underline"
              >
                Edit legend / paragraph
              </button>
              <span className="text-[var(--ink-muted)]">·</span>
              <span className="text-[var(--ink-muted)]">
                Legend: {img.image_legend || "(none)"}
              </span>
              <span className="text-[var(--ink-muted)]">·</span>
              <span className="text-[var(--ink-muted)]">
                Paragraph: {img.paragraph_index}
              </span>
              <ArticleStockButton
                artStock={artStock}
                url={img.url}
                defaultCredit={
                  img.image_legend === "custom" || img.image_legend === "courtesy" ? legendText : ""
                }
                compact
              />
              <button
                data-testid={`body-image-delete-${img.id}`}
                onClick={() => window.confirm("Remove this image?") && del.mutate()}
                className="ml-auto text-red-600 hover:text-red-800 inline-flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </>
          )}
        </div>
      )}
    </figure>
  );
}

function BodyImageEditor({ article, img, courtesyNames = [], onDone }) {
  const qc = useQueryClient();
  const [legend, setLegend] = useState(img.image_legend || "");
  const [customText, setCustomText] = useState(img.image_legend_custom_text || "");
  const [paragraphIndex, setParagraphIndex] = useState(img.paragraph_index ?? 0);
  const save = useMutation({
    mutationFn: () => api.patchBodyImage(article.id, img.id, {
      image_legend: legend || null,
      image_legend_custom_text:
        legend === "custom" || legend === "courtesy" ? customText : null,
      paragraph_index: paragraphIndex,
    }),
    onSuccess: () => {
      toast.success("Image updated");
      qc.invalidateQueries({ queryKey: ["article", article.id] });
      onDone();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  return (
    <div className="w-full flex flex-wrap items-center gap-2">
      <select
        data-testid={`body-image-legend-${img.id}`}
        value={legend}
        onChange={(e) => {
          const next = e.target.value;
          setLegend(next);
          // Same as the featured image: picking "Photo courtesy of…"
          // with no name yet pre-fills the first source the writer
          // used. Text already typed is never replaced.
          const prefill = courtesyPrefill(next, customText, courtesyNames);
          if (prefill) setCustomText(prefill);
        }}
        className="text-[11px] border border-[var(--border)] px-1 py-0.5 bg-white"
      >
        {LEGEND_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {(legend === "custom" || legend === "courtesy") && (
        <input
          data-testid={`body-image-legend-text-${img.id}`}
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          list={
            legend === "courtesy" && courtesyNames.length
              ? `body-image-courtesy-${img.id}`
              : undefined
          }
          placeholder={
            legend === "courtesy" ? "Source name…" : "Photo credit…"
          }
          className="text-[11px] border border-[var(--border)] px-2 py-0.5 bg-white flex-1 min-w-[180px]"
        />
      )}
      {legend === "courtesy" && courtesyNames.length > 0 && (
        <datalist id={`body-image-courtesy-${img.id}`}>
          {courtesyNames.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
      )}
      <label className="text-[10px] text-[var(--ink-muted)] flex items-center gap-1">
        After ¶
        <input
          type="number"
          min="0"
          data-testid={`body-image-paragraph-${img.id}`}
          value={paragraphIndex}
          onChange={(e) => setParagraphIndex(parseInt(e.target.value || "0", 10))}
          className="w-14 text-[11px] border border-[var(--border)] px-1 py-0.5 bg-white"
        />
      </label>
      <button
        data-testid={`body-image-save-${img.id}`}
        onClick={() => save.mutate()}
        disabled={save.isPending}
        className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--slate-900)] text-white disabled:opacity-40"
      >
        {save.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
        Save
      </button>
      <button
        onClick={onDone}
        className="text-[11px] text-[var(--ink-muted)] hover:text-[var(--ink)]"
      >
        Cancel
      </button>
    </div>
  );
}


function AddBodyImageModal({ article, paragraphIndex, onClose }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState("upload");
  const fileRef = useRef(null);
  const [aiSubject, setAiSubject] = useState("");
  const sourceImages = article.source_images || [];

  const onSuccess = () => {
    qc.invalidateQueries({ queryKey: ["article", article.id] });
    toast.success("Body image added — set the legend below the image");
    onClose();
  };
  const onError = (e) => toast.error(e?.response?.data?.detail || "Failed");

  // "Add this photo to Newsdesk Stock" on the Upload tab (and paste).
  const addToStock = useAddToStock();
  // "Save to Stock" on each From-source tile.
  const srcStock = useSourceStock(article);
  const upload = useMutation({
    mutationFn: (file) => api.uploadBodyImage(article.id, file, paragraphIndex),
    onSuccess: (updated, file) => {
      // `$push` appends, so the new body image is the last entry.
      const added = (updated?.body_images || []).slice(-1)[0];
      addToStock.stashIfEnabled({
        file, article: updated, imageUrl: added?.url, kind: "body",
      });
      onSuccess();
    },
    onError,
  });
  const importIt = useMutation({
    mutationFn: (url) => api.importBodyImage(article.id, url, paragraphIndex),
    onSuccess, onError,
  });
  const generate = useMutation({
    mutationFn: () => api.generateBodyImage(article.id, paragraphIndex, aiSubject.trim()),
    onSuccess, onError,
  });

  // Modal-level paste capture, registered for as long as the modal is
  // open — not just while the Upload tab is showing — so Cmd/Ctrl+V
  // never falls through to the page's featured-image handler behind
  // it. Last-in-wins: this is registered after the page's, so it takes
  // the paste, and closing the modal hands it straight back.
  usePasteImage({
    onImage: (file) => upload.mutate(file),
    onError: (msg) => toast.error(msg),
  });

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="body-image-add-modal"
        className={
          "w-full bg-white border border-[var(--border)] shadow-xl max-h-[92vh] flex flex-col " +
          (tab === "stock" ? "max-w-4xl" : "max-w-xl")
        }
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div>
            <div className="overline">Body image</div>
            <h2 className="font-serif-display text-lg">Add after paragraph {paragraphIndex + 1}</h2>
          </div>
          <button onClick={onClose} className="text-[var(--ink-muted)] hover:text-[var(--ink)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="border-b border-[var(--border)] flex">
          {[
            { key: "upload", label: "Upload", icon: Upload },
            { key: "source", label: `From source (${sourceImages.length})`, icon: ImageIcon },
            { key: "stock", label: "Stock", icon: Images },
            { key: "ai", label: "AI generate", icon: Sparkles },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`body-image-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={
                "flex-1 text-xs px-3 py-2 border-b-2 flex items-center justify-center gap-1.5 " +
                (tab === t.key
                  ? "border-[var(--ink)] text-[var(--ink)] font-medium"
                  : "border-transparent text-[var(--ink-muted)] hover:text-[var(--ink)]")
              }
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-5 space-y-3 min-h-[220px] overflow-y-auto">
          {tab === "upload" && (
            <AddToStockOption stock={addToStock} testIdPrefix="body-image" className="pb-1" />
          )}
          {tab === "stock" && (
            // Pexels / Unsplash / Newsdesk sub-tabs come from the picker's
            // own provider tabs (only providers with keys configured).
            <StockPhotoPicker
              article={article}
              onPick={(photo) => api.addBodyStockImage(article.id, photo, paragraphIndex)}
              onPicked={() => onClose()}
              actionLabel="Insert here"
              actionTitle="Insert this photo after the paragraph (credit becomes the legend)"
              successMessage="Stock photo added — adjust the legend below the image"
            />
          )}
          {tab === "upload" && (
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload.mutate(f);
                  e.target.value = "";
                }}
              />
              <button
                data-testid="body-image-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={upload.isPending}
                className="w-full py-8 border-2 border-dashed border-[var(--border)] hover:border-[var(--ink)] flex flex-col items-center justify-center gap-2 disabled:opacity-40"
              >
                {upload.isPending ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Upload className="w-6 h-6" />
                )}
                <span className="text-xs">Click to select an image</span>
                <span className="text-[10px] text-[var(--ink-muted)]">
                  Max 25 MB · resized to 1200px wide · WebP-encoded
                </span>
              </button>
              {/* Paste lands HERE while the modal is open: the paste
                  stack is last-in-wins, so this button's listener sits
                  on top of the article page's featured-image one and
                  hands it back on close. */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <PasteImageButton
                  onImage={(file) => upload.mutate(file)}
                  busy={upload.isPending}
                  hotkey={false}
                  testId="body-image-paste-btn"
                  label="Paste"
                  title="Paste an image from the clipboard into this paragraph slot — or press Cmd/Ctrl+V while this window is open"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-60"
                />
              </div>
            </div>
          )}
          {tab === "source" && (
            sourceImages.length === 0 ? (
              <div className="text-xs text-[var(--ink-muted)] italic py-6 text-center">
                No source images detected for this article.
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 max-h-96 overflow-y-auto">
                {sourceImages.map((u, i) => (
                  <div key={i} className="relative group">
                    <button
                      data-testid={`body-image-source-pick-${i}`}
                      onClick={() => importIt.mutate(u)}
                      disabled={importIt.isPending}
                      className="block w-full aspect-video border border-[var(--border)] hover:border-[var(--ink)] overflow-hidden disabled:opacity-40"
                    >
                      <img src={u} alt="" className="w-full h-full object-cover" />
                    </button>
                    <SourceStockButton
                      srcStock={srcStock}
                      url={u}
                      className={
                        "absolute top-1 right-1 " +
                        (srcStock.savedMap[u] ? "" : "opacity-0 group-hover:opacity-100")
                      }
                    />
                  </div>
                ))}
              </div>
            )
          )}
          {tab === "ai" && (
            <div className="space-y-2">
              <textarea
                data-testid="body-image-ai-subject"
                value={aiSubject}
                onChange={(e) => setAiSubject(e.target.value)}
                rows={3}
                placeholder="Optional subject override — e.g. 'A pod of dolphins near Punta Banda at sunrise, wide shot'. Leave blank to auto-derive from the headline."
                className="w-full text-sm border border-[var(--border)] px-2 py-1.5 bg-white outline-none focus:border-[var(--ink)] resize-y"
              />
              <button
                data-testid="body-image-ai-generate-btn"
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
                className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate-900)] text-white disabled:opacity-40"
              >
                {generate.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                Generate image
              </button>
              <div className="text-[10px] text-[var(--ink-muted)]">
                Uses the same prompt-style + reference-image pipeline as the featured image generator.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
